import React, { useState, useEffect } from 'react';
import { View, Modal } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { dispatchService } from '../../../services/central-api/dispatch';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from './ActingBodiesDropdown';
import EquipmentCategoryDropdown from '../../../components/dispatch/EquipmentCategoryDropdown';
import { useAuth } from '../../../context/Auth';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    equipment?: Equipment;
    authenticatedUser?: AuthenticatedUser;
}

export default function EquipmentsModal({ onClose, isModalVisible, equipment }: ModalProps) {
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState('');
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [equipmentCategory, setEquipmentCategory] = useState<number>(0);

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    function clearValues() {
        setName('');
        setActingBodyId(0);
        setEquipmentCategory(0);
    }

    useEffect(() => {
        if (!equipment || !isModalVisible) {
            return;
        }
        setName(equipment.name);
        setActingBodyId(equipment.actingBodyId);
        setEquipmentCategory(equipment.equipmentCategory?.id || 0);
    }, [equipment, isModalVisible]);

    async function formSubmit() {
        try {
            if (equipment?.id) {
                await dispatchService.updateEquipment({
                    id: equipment.id,
                    name,
                    registry: name,
                    equipmentCategoryId: equipmentCategory !== 0 ? equipmentCategory : undefined
                });
            } else {
                await dispatchService.createEquipment({
                    actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
                    name,
                    registry: name,
                    equipmentCategoryId: equipmentCategory !== 0 ? equipmentCategory : undefined
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            clearValues();
            onClose();
        }
    }

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_equipments) {
            return actingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                clearValues();
                onClose();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                    {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_equipments) &&
                        <ActingBodiesDropdown includeAllOption={false} value={actingBodyId} setValue={setActingBodyId} zIndex={2} disabled={!!equipment?.id} />
                    }
                    <FormInput
                        label={translate('name')}
                        placeholder={translate('name')}
                        value={name}
                        onChangeText={setName}
                        invalid={isNameInvalid}
                    />
                    <EquipmentCategoryDropdown
                        value={equipmentCategory}
                        setValue={setEquipmentCategory}
                        actingBodyId={getActingBodyId()}
                        zIndex={1}
                        disabled={!getActingBodyId()}
                        allowEmpty
                    />
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            clearValues();
                            onClose();
                        }}
                        disabled={!isFormValid()} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
}));
